// face-picking — определение грани (face) под курсором по триангуляции тела.
//
// У нас mesh приходит из replicad как одна BufferGeometry без per-face группировки.
// Чтобы понять «вот это плоскость крышки куба», объединяем все треугольники у
// которых нормаль одинаковая (cosθ > 0.99) И они геометрически соседние (BFS
// через общие вершины).
//
// При hover в r3f мы получаем intersect.face = {a,b,c, normal} — это один
// треугольник. По нему через faceCache находим к какой «большой грани» он
// принадлежит, и возвращаем её bbox в локальных координатах для overlay.

import * as THREE from 'three';

const NORMAL_DOT_THRESHOLD = 0.999;  // косинус угла между нормалями
const PLANE_DIST_EPS = 0.01;         // мм


/**
 * Группирует треугольники mesh по плоским граням.
 * Возвращает { triToFace: Int32Array, faces: [{ normal, origin, triCount, vertSet }, ...] }
 *
 * Кэшировать через useMemo([mesh]) на стороне React.
 */
export function buildFaceGroups(positions, indices, normals) {
    const triCount = (indices.length / 3) | 0;
    const triToFace = new Int32Array(triCount);
    triToFace.fill(-1);

    // Быстрая нормаль треугольника: усреднённая из трёх вершин (если есть)
    // или вычислять из векторов. У replicad нормали — per-vertex, но в плоских
    // гранях все три vertex-normal'и совпадают.
    const triNormal = new THREE.Vector3();
    const triCentroid = new THREE.Vector3();
    const v0 = new THREE.Vector3(), v1 = new THREE.Vector3(), v2 = new THREE.Vector3();
    const e1 = new THREE.Vector3(), e2 = new THREE.Vector3();

    function getTriData(triIdx) {
        const a = indices[triIdx * 3];
        const b = indices[triIdx * 3 + 1];
        const c = indices[triIdx * 3 + 2];
        v0.fromArray(positions, a * 3);
        v1.fromArray(positions, b * 3);
        v2.fromArray(positions, c * 3);
        if (normals && normals.length > 0) {
            triNormal.fromArray(normals, a * 3);
            // Используем нормаль первой вершины — для плоских граней она равна нормали грани.
        } else {
            e1.subVectors(v1, v0);
            e2.subVectors(v2, v0);
            triNormal.crossVectors(e1, e2).normalize();
        }
        triCentroid.copy(v0).add(v1).add(v2).multiplyScalar(1 / 3);
        return { a, b, c };
    }

    // Adjacency: для каждой вершины — список треугольников, в которых она.
    // Это позволит делать BFS по «общая вершина = сосед».
    const vertToTris = new Map();
    for (let t = 0; t < triCount; t++) {
        for (let i = 0; i < 3; i++) {
            const v = indices[t * 3 + i];
            if (!vertToTris.has(v)) vertToTris.set(v, []);
            vertToTris.get(v).push(t);
        }
    }

    const faces = [];

    for (let t = 0; t < triCount; t++) {
        if (triToFace[t] !== -1) continue;
        // Стартуем новую грань с этого треугольника.
        const faceId = faces.length;
        const { a: aSeed } = getTriData(t);
        const seedNormal = triNormal.clone();
        const seedOrigin = triCentroid.clone();
        // Плоскость: pos·normal = d
        const d = seedOrigin.dot(seedNormal);
        const vertSet = new Set();
        let count = 0;

        // BFS
        const stack = [t];
        while (stack.length > 0) {
            const cur = stack.pop();
            if (triToFace[cur] !== -1) continue;
            const { a, b, c } = getTriData(cur);
            // Проверка нормали и плоскости
            if (triNormal.dot(seedNormal) < NORMAL_DOT_THRESHOLD) continue;
            // Проверка что треугольник лежит в той же плоскости
            const dCur = triCentroid.dot(seedNormal);
            if (Math.abs(dCur - d) > PLANE_DIST_EPS) continue;

            triToFace[cur] = faceId;
            count++;
            vertSet.add(a); vertSet.add(b); vertSet.add(c);

            // Соседи через общие вершины
            for (const vi of [a, b, c]) {
                const neighbors = vertToTris.get(vi);
                if (!neighbors) continue;
                for (const nb of neighbors) {
                    if (triToFace[nb] === -1) stack.push(nb);
                }
            }
        }

        // sanity: если по какой-то причине ничего не нашли, используем seed
        if (count === 0) {
            triToFace[t] = faceId;
            count = 1;
            vertSet.add(aSeed);
        }

        /*
         * ПРИЗНАК КРИВОЙ ПОВЕРХНОСТИ.
         *
         * Такую грань нельзя отдавать под выдавливание: профиль получится
         * вырожденным, и OCCT падает («Error: 12209240», затем «This object has
         * been deleted» и потеря тел).
         *
         * 🔴 РАНЬШЕ ЗДЕСЬ СТОЯЛО `count <= 2`, и это было неверно. Считалось,
         * что у плоской грани «минимум несколько десятков треугольников», но
         * плоский ПРЯМОУГОЛЬНИК триангулируется ровно в ДВА треугольника.
         * В результате у обычного бруска кривыми объявлялись ВСЕ ШЕСТЬ граней:
         * клик по любой из них выдавал «Эта поверхность изогнутая» — при том,
         * что ничего изогнутого в сцене не было.
         *
         * Правильный признак — геометрический, а не счётный: грань кривая,
         * если её вершины НЕ лежат в одной плоскости. BFS выше уже собирал
         * только компланарные треугольники, поэтому проверяем сам результат:
         * максимальное отклонение вершин группы от плоскости seed'а.
         * У плоской грани оно нулевое при любом числе треугольников,
         * у куска цилиндра/сферы — заметное.
         */
        let maxDev = 0;
        for (const vi of vertSet) {
            const dev = Math.abs(
                positions[vi * 3] * seedNormal.x
                + positions[vi * 3 + 1] * seedNormal.y
                + positions[vi * 3 + 2] * seedNormal.z
                - d
            );
            if (dev > maxDev) maxDev = dev;
        }

        faces.push({
            faceId,
            normal: seedNormal,
            origin: seedOrigin,
            d,
            triCount: count,
            // Порог тот же, что и у отбора треугольников в BFS: за его пределами
            // точка уже считается лежащей в другой плоскости.
            curved: maxDev > PLANE_DIST_EPS,
            vertSet,
        });
    }

    return { triToFace, faces };
}


/** Вычисляет prepareFaceOverlay — данные для рендера hover-квадрата над гранью.
 *  Возвращает { center: THREE.Vector3, size: {w, h}, quaternion: THREE.Quaternion }
 *  где quaternion ориентирует плейн чтобы его +Z совпадал с нормалью грани. */
export function computeFaceOverlay(positions, face) {
    const n = face.normal;
    // Строим ортонормированный базис (u, v) на плоскости грани.
    // u — любой вектор перпендикулярный n. Берём наименее коллинеарный из X/Y/Z.
    const tmp = new THREE.Vector3();
    if (Math.abs(n.x) < 0.9) tmp.set(1, 0, 0);
    else                     tmp.set(0, 1, 0);
    const u = new THREE.Vector3().crossVectors(tmp, n).normalize();
    const v = new THREE.Vector3().crossVectors(n, u).normalize();

    // Bounding box вершин грани в координатах (u, v).
    let uMin = Infinity, uMax = -Infinity, vMin = Infinity, vMax = -Infinity;
    let cx = 0, cy = 0, cz = 0, count = 0;
    const p = new THREE.Vector3();
    for (const idx of face.vertSet) {
        p.fromArray(positions, idx * 3);
        cx += p.x; cy += p.y; cz += p.z; count++;
        const uu = p.dot(u);
        const vv = p.dot(v);
        if (uu < uMin) uMin = uu;
        if (uu > uMax) uMax = uu;
        if (vv < vMin) vMin = vv;
        if (vv > vMax) vMax = vv;
    }
    if (count === 0) return null;

    // Центр грани = центр bbox в (u,v), спроецированный обратно в 3D.
    // Это даёт корректный центр прямоугольной грани (ребро/середина куба).
    const uCenter = (uMin + uMax) / 2;
    const vCenter = (vMin + vMax) / 2;
    // d = origin·n — расстояние плоскости от 0
    const center = new THREE.Vector3()
        .addScaledVector(u, uCenter)
        .addScaledVector(v, vCenter)
        .addScaledVector(n, face.d);

    const w = uMax - uMin;
    const h = vMax - vMin;

    // Quaternion: матрица где X=u, Y=v, Z=n
    const m = new THREE.Matrix4().makeBasis(u, v, n);
    const quaternion = new THREE.Quaternion().setFromRotationMatrix(m);

    return {
        center,
        size: { w, h },
        quaternion,
        u, v, normal: n.clone(),
    };
}


/**
 * Извлекает контур грани (boundary loop) в 2D-координатах плоскости (u, v).
 *
 * Алгоритм:
 *   1. Собираем все рёбра треугольников этой грани, считаем сколько раз каждое
 *      встречается. Внутренние рёбра (общие для двух треугольников грани)
 *      встречаются 2 раза, граничные — 1 раз.
 *   2. Граничные рёбра соединяем в замкнутый цикл через общие вершины.
 *   3. Проецируем точки цикла на плоскость (u, v) и отдаём массив { x, y }
 *      в локальных координатах эскиза.
 *
 * Возвращает:
 *   { verts2d: [{x,y}, ...], planeUV: { u, v, planePos: Vector3 } } | null
 *
 * planePos — точка на плоскости (origin плоскости replicad в мире). Используем
 * центр грани, спроецированный на плоскость.
 *
 * verts2d — координаты вершин грани в системе плоскости sketchOnPlane,
 * подготовленные для подачи в extrudeSketchProfile (см. profilePolygonVertices).
 *
 * ВАЖНО: для extrude нужны координаты в системе ИМЕННО replicad-плоскости
 * (XY/XZ/YZ), а не в произвольной (u,v). После того как мы знаем что грань
 * параллельна одной из главных осей (faceToPlaneRef в FacePicker.jsx),
 * координаты в этой плоскости можно получить как:
 *   - 'XY': (x, y) = (point.x, point.y)
 *   - 'XZ': (x, y) = (point.x, point.z)
 *   - 'YZ': (x, y) = (point.y, point.z)
 * Знак Y зависит от того смотрит ли нормаль грани в +Z/Y/X или -Z/Y/X.
 *
 * Эта функция не знает про XY/XZ/YZ — она просто возвращает контур в 3D
 * (массив Vector3). Конвертация в 2D-плоскость делается уже в caller'е,
 * где известен planeId.
 */
export function extractFaceLoop(positions, indices, face, triToFace) {
    const faceTriIds = [];
    for (let t = 0; t < triToFace.length; t++) {
        if (triToFace[t] === face.faceId) faceTriIds.push(t);
    }
    // Если faceId не записан в face — найдём его сами через сравнение объектов.
    // Простой fallback: face.vertSet уже есть → собираем все рёбра треугольников
    // которые целиком в vertSet.
    const triList = faceTriIds.length > 0 ? faceTriIds : (() => {
        const list = [];
        for (let t = 0; t < indices.length / 3; t++) {
            const a = indices[t * 3], b = indices[t * 3 + 1], c = indices[t * 3 + 2];
            if (face.vertSet.has(a) && face.vertSet.has(b) && face.vertSet.has(c)) {
                list.push(t);
            }
        }
        return list;
    })();

    if (triList.length === 0) return null;

    // Собираем рёбра в Map: key="min-max", value=count
    // edgeKey возвращает строку чтобы Map работал детерминированно.
    const edgeKey = (a, b) => (a < b ? `${a}-${b}` : `${b}-${a}`);
    const edges = new Map();
    for (const t of triList) {
        const a = indices[t * 3];
        const b = indices[t * 3 + 1];
        const c = indices[t * 3 + 2];
        for (const [u, v] of [[a, b], [b, c], [c, a]]) {
            const k = edgeKey(u, v);
            edges.set(k, (edges.get(k) || 0) + 1);
        }
    }

    // Граничные рёбра — те которые встречаются 1 раз
    const boundary = [];
    for (const [k, count] of edges) {
        if (count === 1) {
            const [a, b] = k.split('-').map(Number);
            boundary.push([a, b]);
        }
    }
    if (boundary.length < 3) return null;

    // Строим adj: vertex -> [neighbour vertices via boundary edges]
    const adj = new Map();
    for (const [a, b] of boundary) {
        if (!adj.has(a)) adj.set(a, []);
        if (!adj.has(b)) adj.set(b, []);
        adj.get(a).push(b);
        adj.get(b).push(a);
    }

    // Идём по циклу начиная с любой вершины. У хорошего mesh каждая
    // boundary-вершина имеет ровно 2 соседа. Если есть «дыры» в грани
    // (например цилиндрическое отверстие) — будет несколько loop'ов.
    // Для MVP берём ВНЕШНИЙ контур: тот у которого bbox в плоскости (u,v) больше.
    const loops = [];
    const visited = new Set();
    for (const start of adj.keys()) {
        if (visited.has(start)) continue;
        const loop = [start];
        visited.add(start);
        let prev = -1;
        let cur = start;
        /*
         * ⚠️ ЦИКЛ ОБЯЗАН ЗАМКНУТЬСЯ.
         *
         * Раньше выход был по `!visited.has(n)` — обход шёл в одну сторону и
         * обрывался, а если старт пришёлся на середину контура, вторая половина
         * терялась. close() схлопывал такую дугу хордой, контур получал
         * перехлёст, и OCCT падал числовым кодом.
         *
         * Теперь возврат в start — законное завершение (closed=true), а
         * оборванную цепочку мы отбрасываем: битый контур хуже отказа.
         */
        let closed = false;
        while (true) {
            const neighbours = adj.get(cur) || [];
            // Вернуться в старт можно только пройдя контур целиком, а не сразу
            // назад по тому же ребру.
            if (loop.length >= 3 && neighbours.includes(start) && prev !== start) {
                closed = true;
                break;
            }
            const next = neighbours.find((n) => n !== prev && !visited.has(n));
            if (next == null) break;
            loop.push(next);
            visited.add(next);
            prev = cur;
            cur = next;
        }
        if (closed && loop.length >= 3) loops.push(loop);
    }
    if (loops.length === 0) return null;

    // Из вершин в 3D-точки
    const loopsAsPoints = loops.map((loop) =>
        loop.map((idx) => {
            const v = new THREE.Vector3();
            v.fromArray(positions, idx * 3);
            return v;
        })
    );

    // Внешний контур = тот у которого bbox-area наибольшая.
    const n = face.normal;
    const tmp = new THREE.Vector3();
    if (Math.abs(n.x) < 0.9) tmp.set(1, 0, 0);
    else                     tmp.set(0, 1, 0);
    const uVec = new THREE.Vector3().crossVectors(tmp, n).normalize();
    const vVec = new THREE.Vector3().crossVectors(n, uVec).normalize();

    let bestIdx = 0;
    let bestArea = -1;
    for (let i = 0; i < loopsAsPoints.length; i++) {
        let uMin = Infinity, uMax = -Infinity, vMin = Infinity, vMax = -Infinity;
        for (const p of loopsAsPoints[i]) {
            const uu = p.dot(uVec), vv = p.dot(vVec);
            if (uu < uMin) uMin = uu; if (uu > uMax) uMax = uu;
            if (vv < vMin) vMin = vv; if (vv > vMax) vMax = vv;
        }
        const area = (uMax - uMin) * (vMax - vMin);
        if (area > bestArea) { bestArea = area; bestIdx = i; }
    }

    /*
     * ⚠️ ВНУТРЕННИЕ КОНТУРЫ ТОЖЕ ОТДАЁМ.
     *
     * У кольцевой грани (торец трубы, дно коробки с отверстием) контуров
     * два: внешний и внутренний. Раньше возвращался только внешний, и
     * «Выдавить грань» строило СПЛОШНОЙ круг вместо кольца. При операции
     * «Вырезать» OCCT получал вырожденную задачу и падал с «This object
     * has been deleted», а на экран сыпались десятки одинаковых ошибок.
     *
     * Теперь внутренние контуры идут в holes3d — вызывающий передаёт их
     * как дырки профиля, и грань выдавливается такой, какая она есть.
     */
    const holes3d = loopsAsPoints.filter((_, i) => i !== bestIdx);

    return {
        points3d: loopsAsPoints[bestIdx],
        holes3d: holes3d.length ? holes3d : null,
        normal: n.clone(),
    };
}
